import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import { validateAndFormatDate } from '../../../../../models/UiUtils';
import { useCreateInsurancePolicyMutation, useUpdateInsurancePolicyMutation } from '../../../../../queries';
import { SimpleAutoCompleteSearchField } from '../../../../components/autocomplete/SimpleAutoCompleteSearchField';
import { CloseButton } from '../../../../components/CloseButton';
import { PolicyCarrierSearch } from '../../../../components/dataBoards/policies/PolicyCarrierSearch';
import { PolicyLineOfBusinessSearch } from '../../../../components/dataBoards/policies/PolicyLineOfBusinessSearch';
import { Modal } from '../../../../components/Modal';
import { PolicyIcon } from '../../../../components/svgs/icons/PolicyIcon';
import { TextInput } from '../../../../components/TextInput';
import { styleSheet as dbStyleSheet } from '../../../../pages/DataBoards/styles';
import { white } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from '../../EditModal/styles';

export const EditPolicyModal = ({
	initialPolicy,
	isOpen,
	setIsOpen,
	onSave,
	onCancel,
}: {
	initialPolicy: Api.IInsurancePolicy;
	isOpen: boolean;
	setIsOpen: (newValue: boolean) => void;
	onSave: (policy: Api.IInsurancePolicy) => void;
	onCancel: () => void;
}) => {
	const [contactName, setContactName] = React.useState(initialPolicy.contact?.handle ?? '');
	const [contact, setContact] = React.useState<Api.IContact>(initialPolicy.contact ?? null);
	const [owner, setOwner] = React.useState<Api.IContact>(initialPolicy.owner ?? null);
	const [premium, setPremium] = React.useState<string>(initialPolicy.premium?.toString() ?? '');
	const [carrier, setCarrier] = React.useState<Api.IInsurancePolicyCarrier>(initialPolicy.carrier ?? null);
	const [policyNumber, setPolicyNumber] = React.useState<string>(initialPolicy.policyNumber ?? '');
	const [commissions, setCommissions] = React.useState<string>(initialPolicy.commissions?.toString() ?? '');
	const [servicingRole1User, setServicingRole1User] = React.useState<Api.IContact>(
		initialPolicy.servicingRole1User ?? null
	);
	const [servicingRole2User, setServicingRole2User] = React.useState<Api.IContact>(
		initialPolicy.servicingRole2User ?? null
	);
	const [lineOfBusiness, setLineOfBusiness] = React.useState<Api.IInsurancePolicyLineOfBusiness>(
		initialPolicy.lineOfBusiness ?? null
	);
	const [renewalDate, setRenewalDate] = React.useState<string>(
		initialPolicy.renewalDate ? moment(initialPolicy.renewalDate).format('MM/DD/YYYY') : ''
	);
	const [effectiveDate, setEffectiveDate] = React.useState<string>(
		initialPolicy.effectiveDate ? moment(initialPolicy.effectiveDate).format('MM/DD/YYYY') : ''
	);
	const [originationDate, setOriginationDate] = React.useState<string>(
		initialPolicy.originationDate ? moment(initialPolicy.originationDate).format('MM/DD/YYYY') : ''
	);
	const [renewalDateError, setRenewalDateError] = React.useState<string>('');
	const [effectiveDateError, setEffectiveDateError] = React.useState<string>('');
	const [originationDateError, setOriginationDateError] = React.useState<string>('');

	const isEditing = !!initialPolicy?.id;

	const handleDateChange = (value: string, setDate: React.Dispatch<React.SetStateAction<string>>) => {
		setDate(value);
	};

	const handleDateBlur = (
		value: string,
		setDate: React.Dispatch<React.SetStateAction<string>>,
		setError: React.Dispatch<React.SetStateAction<string>>
	) => {
		if (!value) {
			setError('');
			return;
		}

		const { isValid, formattedDate } = validateAndFormatDate(value);

		if (isValid) {
			setDate(formattedDate);
			setError('');
		} else {
			setError('Please enter a valid date (MM/DD/YYYY format)');
		}
	};

	const createPolicyMutation = useCreateInsurancePolicyMutation({
		onSuccess: (policy: Api.IInsurancePolicy) => {
			toast.success('Policy successfully created');
			onSave(policy);
		},
	});

	const updatePolicyMutation = useUpdateInsurancePolicyMutation({
		onSuccess: (policy: Api.IInsurancePolicy) => {
			toast.success('Policy successfully updated');
			onSave(policy);
		},
	});

	const save = () => {
		const carrierId = carrier?.id;
		const lineOfBusinessId = lineOfBusiness?.id;
		const ownerId = owner.id;
		const contactId = contact.id;

		const updatedPolicy = {
			...initialPolicy,
			premium: parseFloat(premium),
			carrierId,
			renewalDate: renewalDate ? moment(renewalDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
			effectiveDate: effectiveDate ? moment(effectiveDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
			originationDate: originationDate ? moment(originationDate, 'MM/DD/YYYY').format('YYYY-MM-DD') : null,
			policyNumber,
			contactId,
			lineOfBusinessId,
			ownerId,
			commissions: commissions != null ? parseFloat(commissions) : null,
			servicingRole1UserId: servicingRole1User?.id,
			servicingRole2UserId: servicingRole2User?.id,
		};

		const payload = { policy: updatedPolicy };
		if (isEditing) {
			updatePolicyMutation.mutateAsync(payload);
			return;
		}
		createPolicyMutation.mutateAsync(payload);
	};

	const cancel = () => {
		onCancel();
		setIsOpen(false);
	};

	const onContactCleared = () => {
		if (contactName === getDisplayName(contact)) {
			setContactName('');
		}

		setContact(null);
	};
	const onContactSelected = ({ selection }: { selection: Api.IEntity }) => {
		setContact(selection);

		if (!contactName) {
			setContactName(getDisplayName(selection));
		}
		if (!owner) {
			// TODO: Fetch the owner and set it on the policy if not already set
		}
	};

	const onOwnerCleared = () => {
		setOwner(null);
	};

	const onOwnerSelected = ({ selection }: { selection: Api.IEntity }) => {
		setOwner(selection);
	};

	const onServicingRole1UserCleared = () => {
		setServicingRole1User(null);
	};

	const onServicingRole1UserSelected = ({ selection }: { selection: Api.IEntity }) => {
		setServicingRole1User(selection);
	};

	const onServicingRole2UserCleared = () => {
		setServicingRole2User(null);
	};

	const onServicingRole2UserSelected = ({ selection }: { selection: Api.IEntity }) => {
		setServicingRole2User(selection);
	};

	const selectPolicyLineOfBusiness = (selectedLineOfBusiness: Api.IInsurancePolicyLineOfBusiness) => {
		setLineOfBusiness(selectedLineOfBusiness);
	};

	const selectCarrier = (selectedCarrier: Api.IInsurancePolicyCarrier) => {
		setCarrier(selectedCarrier);
	};

	const queryIsLoading = updatePolicyMutation.isPending || createPolicyMutation.isPending;
	const canSave =
		Boolean(contactName) &&
		Boolean(contact) &&
		Boolean(owner) &&
		parseFloat(premium) >= 0 &&
		Boolean(carrier) &&
		Boolean(lineOfBusiness) &&
		Boolean(effectiveDate) &&
		validateAndFormatDate(effectiveDate).isValid &&
		Boolean(renewalDate) &&
		validateAndFormatDate(renewalDate).isValid &&
		Boolean(originationDate) &&
		validateAndFormatDate(originationDate).isValid &&
		!renewalDateError &&
		!effectiveDateError &&
		!originationDateError &&
		!queryIsLoading;

	const onClose = () => {
		onCancel();
		setIsOpen(false);
	};

	return (
		<Modal
			className={css(styleSheet.modalContainer)}
			isOpen={isOpen}
			headerAccessory={
				<div className={css(styleSheet.modalHeader)}>
					<PolicyIcon height={18} width={20} />
					<CloseButton onClick={onClose} fillColor={white} />
				</div>
			}
			onRequestClose={cancel}
		>
			<article className={css(styleSheet.container, styleSheet.wideContainer)}>
				<h3 className={css(styleSheet.header)}>{`${isEditing ? 'Edit' : 'Add New'} Policy`}</h3>

				<div className={css(styleSheet.twoColumnLayout)}>
					<div className={css(styleSheet.column)}>
						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Policy Holder Name / Company</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<SimpleAutoCompleteSearchField
									onClear={onContactCleared}
									// @ts-ignore
									onItemSelected={onContactSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search'
									style={styleSheet.contactSearchField}
									type={Api.ResourceAutoCompleteViewModelType.Contact}
									initialSearchQuery={initialPolicy.contact ? getDisplayName(initialPolicy.contact) : ''}
									anchorStyles={[dbStyleSheet.searchFieldReset]}
								/>
							</div>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Policy Owner</label>
							<SimpleAutoCompleteSearchField
								onClear={onOwnerCleared}
								// @ts-ignore
								onItemSelected={onOwnerSelected}
								resultsLimit={5}
								pageSize={5}
								placeholder='Search'
								type={Api.ResourceAutoCompleteViewModelType.User}
								initialSearchQuery={initialPolicy.owner ? getDisplayName(initialPolicy.owner) : ''}
								anchorStyles={[dbStyleSheet.searchFieldReset]}
							/>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Line of Business</label>

							<PolicyLineOfBusinessSearch
								onSelectLineOfBusiness={selectPolicyLineOfBusiness}
								initialSearchFragment={lineOfBusiness?.name ?? ''}
								onCreateLineOfBusiness={selectPolicyLineOfBusiness}
								className={css(baseStyleSheet.fullWidth)}
							/>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Carrier</label>

							<PolicyCarrierSearch
								onSelectCarrier={selectCarrier}
								initialSearchFragment={initialPolicy?.carrier?.name ?? ''}
								onCreateCarrier={selectCarrier}
								className={css(baseStyleSheet.fullWidth)}
							/>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Premium Amount</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-premium'
									onChange={e => setPremium(e.target.value)}
									type='number'
									value={premium}
									placeholder='$0'
									min={0}
								/>
							</div>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Policy CSR</label>
							<SimpleAutoCompleteSearchField
								onClear={onServicingRole1UserCleared}
								// @ts-ignore
								onItemSelected={onServicingRole1UserSelected}
								resultsLimit={5}
								pageSize={5}
								placeholder='Search'
								type={Api.ResourceAutoCompleteViewModelType.User}
								initialSearchQuery={
									initialPolicy.servicingRole1User ? getDisplayName(initialPolicy.servicingRole1User) : ''
								}
								anchorStyles={[dbStyleSheet.searchFieldReset]}
							/>
						</div>
					</div>

					<div className={css(styleSheet.column)}>
						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Policy Number</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-policy-number'
									onChange={e => setPolicyNumber(e.target.value)}
									type='text'
									value={policyNumber}
								/>
							</div>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Origination Date</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-origination-date'
									onChange={e => handleDateChange(e.target.value, setOriginationDate)}
									onBlur={e => handleDateBlur(e.target.value, setOriginationDate, setOriginationDateError)}
									type='text'
									value={originationDate}
									placeholder='MM/DD/YYYY'
								/>
							</div>
							{originationDateError && <div className={css(styleSheet.errorText)}>{originationDateError}</div>}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Effective Date</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-effective-date'
									onChange={e => handleDateChange(e.target.value, setEffectiveDate)}
									onBlur={e => handleDateBlur(e.target.value, setEffectiveDate, setEffectiveDateError)}
									type='text'
									value={effectiveDate}
									placeholder='MM/DD/YYYY'
								/>
							</div>
							{effectiveDateError && <div className={css(styleSheet.errorText)}>{effectiveDateError}</div>}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Renewal Date</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-renewal-date'
									onChange={e => handleDateChange(e.target.value, setRenewalDate)}
									onBlur={e => handleDateBlur(e.target.value, setRenewalDate, setRenewalDateError)}
									type='text'
									value={renewalDate}
									placeholder='MM/DD/YYYY'
								/>
							</div>
							{renewalDateError && <div className={css(styleSheet.errorText)}>{renewalDateError}</div>}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Commissions</label>

							<div className={css(styleSheet.formControlInputWrap)}>
								<TextInput
									inputId='policy-modal-edit-commissions'
									onChange={e => setCommissions(e.target.value)}
									type='number'
									value={commissions}
									placeholder='$0'
									min={0}
								/>
							</div>
						</div>

						<div className={css(styleSheet.formControl)}>
							<label className={css(styleSheet.label)}>Policy Producer</label>
							<SimpleAutoCompleteSearchField
								onClear={onServicingRole2UserCleared}
								// @ts-ignore
								onItemSelected={onServicingRole2UserSelected}
								resultsLimit={5}
								pageSize={5}
								placeholder='Search'
								type={Api.ResourceAutoCompleteViewModelType.User}
								initialSearchQuery={
									initialPolicy.servicingRole2User ? getDisplayName(initialPolicy.servicingRole2User) : ''
								}
								anchorStyles={[dbStyleSheet.searchFieldReset]}
							/>
						</div>
					</div>
				</div>

				<footer className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={save} disabled={!canSave}>
						Save
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={cancel}>
						Cancel
					</button>
				</footer>
			</article>
		</Modal>
	);
};
